/**
 * ListItemRow — canonical row chrome for every list-based workspace
 * surface (Conversazioni, Documenti, Lavoro, Finanza, …).
 *
 * Decomposes the recurring "avatar + eyebrow + title + preview + pills
 * + hover-revealed actions" structure into ONE primitive so each tab
 * stays a thin wrapper specialising the slots, NOT re-implementing the
 * wrapper, the spacing, the hover transitions, the a11y wiring, the
 * stagger animation, the bg ramp, or the selection chrome.
 *
 * Visual contract (June 2026 uniformity pass):
 *
 *  ┌────────────────────────────────────────────────────────────────────┐
 *  │ ☐  [Avatar]  EYEBROW · meta · time                       [Actions] │
 *  │              Title (bold = highlighted, medium otherwise)          │
 *  │              1-line preview ink-3                                  │
 *  │              [Pills · Pills · Pills]                               │
 *  └────────────────────────────────────────────────────────────────────┘
 *
 * Slots
 * -----
 *   :prop:`avatar`   — pre-rendered 28×28 chip (typically :class:`SenderAvatar`).
 *   :prop:`eyebrow`  — uppercase 9px text on the eyebrow line.
 *   :prop:`title`    — bolded or medium body text.
 *   :prop:`preview`  — optional 1-line preview text below the title.
 *   :prop:`pills`    — optional row of inline chips (Indicizzata,
 *                      Allegato, sized).
 *   :prop:`actions`  — optional hover-revealed action cluster on the
 *                      right (Star/Read/Snooze/Archive/Reply…).
 *   :prop:`trailing` — optional always-visible right-aligned content
 *                      (clock + unread dot, +N siblings count, etc).
 *
 * State
 * -----
 *   :prop:`selected`     — applies the accent stripe + tinted bg.
 *   :prop:`selectionActive` — surfaces the leading checkbox even when
 *                              the row itself is not selected.
 *   :prop:`highlighted`  — title becomes bold + row sits on ``bg-white``
 *                          (Conversations: ``is_unread``; Documents:
 *                          ``indexing_status !== "completed"``).
 *
 * Selection
 * ---------
 *   :prop:`onToggleSelect` — when provided, renders a hover/active
 *                            checkbox at the leading edge. Stop-bubble
 *                            is handled internally so the row's
 *                            ``onClick`` doesn't fire.
 *
 * Keyboard
 * --------
 *   Pressing ``Space`` on the focused button toggles selection (matches
 *   the Gmail-style ``j/k/Enter/Space`` map driven by
 *   :func:`useListKeyboardNav`). The wrapping ``<li>`` is given
 *   ``role="option"`` + ``aria-selected`` so the outer list's
 *   ``role="listbox"`` AT semantics work out of the box.
 */

import { Check } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";


export interface ListItemRowProps {
  /** Stable id used as ``data-item-id`` + the checkbox aria-label. */
  readonly itemId: string;
  /**
   * Stagger index — drives the 40ms-per-row entrance animation
   * (capped at idx<20 to keep the wave snappy on long lists).
   */
  readonly index: number;
  /** Rendered to the LEFT of the title column. Typically a 28×28 avatar. */
  readonly avatar: React.ReactNode;
  /**
   * Eyebrow line content — caller composes ``"EMAIL · Mario · 31m"``
   * style strings. ``aria-hidden`` not applied here: the eyebrow gives
   * scan-ability for sighted users AND additional context to AT users.
   */
  readonly eyebrow: React.ReactNode;
  /** Title — required, can be ReactNode for inline icons (Pin glyph). */
  readonly title: React.ReactNode;
  /** Optional 1-line preview below the title. */
  readonly preview?: React.ReactNode;
  /** Optional inline chips row under the preview. */
  readonly pills?: React.ReactNode;
  /** Optional hover-revealed action cluster on the right. */
  readonly actions?: React.ReactNode;
  /**
   * Optional always-visible trailing content rendered on the eyebrow
   * line's right side. Use for the time + status dot in inbox or for
   * the pin + size in documents.
   */
  readonly trailing?: React.ReactNode;
  /** Required body click handler — typically opens the detail view. */
  readonly onClick: () => void;
  /**
   * Optional hover/focus hook — fires on pointer-enter AND keyboard focus of
   * the row. Callers use it to PREFETCH the detail the click will open (e.g.
   * the email thread), so opening is instant. Purely a warm-up; must be
   * idempotent + side-effect-free beyond cache priming.
   */
  readonly onPointerEnter?: () => void;
  /** When provided, renders the checkbox at the leading edge. */
  readonly onToggleSelect?: () => void;
  readonly selected?: boolean;
  readonly selectionActive?: boolean;
  /** ``true`` → bold title + ``bg-white``; ``false`` → medium + ink wash. */
  readonly highlighted?: boolean;
  /** ``true`` → render the row in the "focused/active" inspect state. */
  readonly isActive?: boolean;
  /** Aria-label for the checkbox — falls back to the title when omitted. */
  readonly checkboxLabel?: string;
  /** Optional extra classes merged on the outer ``<li>``. */
  readonly className?: string;
  /** Optional data-attribute for IO-based lazy fetches (e.g. AI summary). */
  readonly dataMessageId?: string;
  /**
   * Optional disclosure state — when set, the outer ``<li>`` exposes
   * ``aria-expanded`` so screen readers announce the expansion of an
   * adjacent details block (e.g. a nudge cluster's source list). The
   * row body's ``onClick`` is expected to toggle this externally —
   * ListItemRow itself does not own the open/closed state.
   */
  readonly ariaExpanded?: boolean;
  /**
   * Optional ``aria-controls`` value — pairs with ``ariaExpanded`` to
   * tell screen readers WHICH element this row controls. Match the
   * ``id`` of the disclosed region rendered by the parent.
   */
  readonly ariaControls?: string;
  /**
   * Optional aria-label for the row's primary action button. Use when
   * the visible content doesn't convey what the click WILL DO (e.g.
   * "Completa <title>" on a task row whose visible avatar is purely
   * decorative). Without it, the row content is used as accessible name.
   */
  readonly actionLabel?: string;
  /**
   * When ``true``, the row's primary action button shows a disabled
   * affordance: ``aria-busy`` + ``aria-disabled`` + reduced opacity.
   * Use during in-flight mutations (toggle / complete / dismiss) so
   * the user sees that the click WAS received and a second click is
   * suppressed. The kebab/actions overlay is NOT dimmed — caller
   * should ``disabled={isBusy}`` on individual action buttons.
   */
  readonly isBusy?: boolean;
}


export const ListItemRow = React.forwardRef<HTMLLIElement, ListItemRowProps>(
  function ListItemRow(props, ref) {
    const {
      itemId,
      index,
      avatar,
      eyebrow,
      title,
      preview,
      pills,
      actions,
      trailing,
      onClick,
      onPointerEnter,
      onToggleSelect,
      selected = false,
      selectionActive = false,
      highlighted = false,
      isActive = false,
      checkboxLabel,
      className,
      dataMessageId,
      ariaExpanded,
      ariaControls,
      actionLabel,
      isBusy = false,
    } = props;
    const animationDelay = `${240 + Math.min(index, 19) * 40}ms`;

    return (
      <li
        ref={ref}
        style={{ animationDelay }}
        data-item-id={itemId}
        data-message-id={dataMessageId}
        data-selected={selected ? "true" : undefined}
        aria-selected={selected}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        onPointerEnter={onPointerEnter}
        onFocus={onPointerEnter}
        className={cn(
          "group relative animate-item-in border-black/5 border-b last:border-b-0 motion-reduce:animate-none",
          "hover:z-10",
          // Read/unread (highlighted/handled) ramp — bold rows on white,
          // handled rows on a faint ink wash.
          highlighted ? "bg-white" : "bg-ink-1/[0.02]",
          // Active = the currently-focused / inspected row. Gentle wash
          // that doesn't compete with the selection accent.
          isActive && "bg-white/55",
          // Selected = part of the bulk-selection set. Accent stripe on
          // the left edge + accent-tinted bg.
          selected &&
            "bg-accent/[0.06] before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-r-full before:bg-accent",
          className,
        )}
      >
        {onToggleSelect ? (
          <label
            className={cn(
              // Overlaid on the avatar (same spot) so revealing it never shifts
              // the row content sideways — the avatar fades out beneath it. The
              // whole 28px label toggles; pointer-events are off while hidden so
              // a resting row still opens on click.
              "absolute top-1/2 left-5 flex h-7 w-7 items-center justify-center transition-opacity duration-fast",
              "-translate-y-1/2 cursor-pointer",
              selectionActive || selected
                ? "opacity-100"
                : "pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100",
            )}
          >
            <input
              type="checkbox"
              checked={selected}
              // Not a tab stop: keyboard selection is the row button's Space
              // handler. Blur after toggling so a mouse click doesn't leave the
              // checkbox stuck visible (focus-within) after deselecting.
              tabIndex={-1}
              onChange={(e) => {
                onToggleSelect();
                e.currentTarget.blur();
              }}
              onClick={(e) => e.stopPropagation()}
              aria-label={checkboxLabel ?? `Seleziona ${itemId}`}
              className="peer sr-only"
            />
            <span
              aria-hidden
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-accent/50",
                selected
                  ? "border-accent bg-accent text-white"
                  : "border-ink-3/40 bg-white/80 text-transparent",
              )}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
          </label>
        ) : null}

        <button
          type="button"
          aria-current={isActive ? "true" : undefined}
          aria-label={actionLabel}
          aria-busy={isBusy || undefined}
          aria-disabled={isBusy || undefined}
          onClick={isBusy ? undefined : onClick}
          onKeyDown={(e) => {
            // Space toggles selection when the parent provided a
            // handler. Matches the Gmail keyboard idiom.
            if (e.key === " " && onToggleSelect) {
              e.preventDefault();
              onToggleSelect();
            }
          }}
          className={cn(
            "flex w-full cursor-pointer items-center gap-3 py-3.5 pr-8 pl-5 text-left transition-[background,box-shadow] duration-200",
            "hover:shadow-[0_1px_2px_rgba(60,40,20,0.05),0_3px_8px_-2px_rgba(60,40,20,0.10)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
            // Busy state: dim + suppress hover so the user sees the
            // click was received and a second one is queued out.
            isBusy && "cursor-wait opacity-60 hover:bg-transparent",
          )}
        >
          <span
            className={cn(
              // Decorative — never captures clicks (when faded it is promoted
              // above the checkbox, so it must not steal its pointer events).
              "pointer-events-none inline-flex shrink-0 transition-opacity duration-fast",
              onToggleSelect &&
                (selected || selectionActive
                  ? "opacity-0"
                  : "group-focus-within:opacity-0 group-hover:opacity-0"),
            )}
          >
            {avatar}
          </span>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-2">
              {eyebrow}
              {trailing ? (
                <span className="ml-auto flex shrink-0 items-center gap-1.5">
                  {trailing}
                </span>
              ) : null}
            </div>
            <div
              className={cn(
                "truncate text-[13px] text-ink-1",
                highlighted ? "font-semibold" : "font-medium",
              )}
            >
              {title}
            </div>
            {preview ? (
              <div className="mt-0.5 truncate text-[12px] text-ink-3">
                {preview}
              </div>
            ) : null}
            {pills ? (
              <div className="mt-1.5 flex items-center gap-1.5">{pills}</div>
            ) : null}
          </div>
        </button>

        {actions ? (
          <div
            role="toolbar"
            aria-label="Azioni rapide"
            className={cn(
              // Naked icons — the resting meta (time/dot) fades out beneath them
              // (see InboxItemRow), so no chip or gradient is needed and this
              // reads cleanly on any row background.
              "pointer-events-none absolute top-2.5 right-8 flex items-center gap-0.5 opacity-0",
              "transition-opacity duration-fast",
              "group-hover:pointer-events-auto group-hover:opacity-100",
              "group-focus-within:pointer-events-auto group-focus-within:opacity-100",
            )}
          >
            {actions}
          </div>
        ) : null}
      </li>
    );
  },
);
ListItemRow.displayName = "ListItemRow";


// ── Sub-primitives ────────────────────────────────────────────────────


/**
 * Compact tonal button used inside :prop:`ListItemRow.actions`.
 * Replicates the ``RowActionButton`` styling used by InboxItemRow so a
 * future caller doesn't reinvent the dimensions/hover.
 */
export interface ListItemActionButtonProps {
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly onClick: (event: React.MouseEvent) => void;
  readonly className?: string;
}


export const ListItemActionButton: React.FC<ListItemActionButtonProps> = ({
  label,
  icon,
  onClick,
  className,
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
    className={cn(
      // Visual 24×24px (icon + bg) but the click area extends to 32×32 via
      // an invisible inset hit-slop, so touch targets clear the 24px WCAG
      // 2.5.5 AA baseline + the 32px comfort floor without bloating the
      // visible chip cluster (Gmail/Linear-style hover row stays dense).
      "before:-inset-1 relative flex h-6 w-6 items-center justify-center rounded-full text-ink-2 transition-colors before:absolute before:content-[''] hover:bg-black/[0.05] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
      className,
    )}
  >
    {icon}
  </button>
);
ListItemActionButton.displayName = "ListItemActionButton";


/**
 * Eyebrow text — uppercase 9px ink-3 (or branded when ``tone=branded``)
 * with optional leading icon. Wraps the recurring pattern so every row
 * eyebrow looks like every other row eyebrow.
 *
 * Example:
 *   <ListItemEyebrow icon={<MessageSquare size={9} />}>
 *     EMAIL · Mario Rossi
 *   </ListItemEyebrow>
 */
export interface ListItemEyebrowProps {
  readonly children: React.ReactNode;
  readonly icon?: React.ReactNode;
  /** ``branded`` → ``text-ando-orange`` (PEC, AI-generated docs). */
  readonly tone?: "neutral" | "branded";
  readonly className?: string;
}


export const ListItemEyebrow: React.FC<ListItemEyebrowProps> = ({
  children,
  icon,
  tone = "neutral",
  className,
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 font-medium text-[9px] uppercase tracking-[0.05em]",
      tone === "branded" ? "text-ando-orange" : "text-ink-3",
      className,
    )}
  >
    {icon}
    {children}
  </span>
);
ListItemEyebrow.displayName = "ListItemEyebrow";
